/**
 * Debug geometry rendering — lines, spheres, boxes.
 * Geometry is cleared every frame (transient debug visualization).
 */

import * as THREE from 'three/webgpu';
import { Logger } from '../core/Logger.js';

const DEBUG_LAYER = 31;
const MAX_LINE_POINTS = 10000;

export class DebugDraw {
  private linePositions: number[] = [];
  private lineColors: number[] = [];
  private lineMesh: THREE.LineSegments | null = null;
  private lineGeometry: THREE.BufferGeometry;
  private lineMaterial: THREE.LineBasicMaterial;
  private _posAttr: THREE.Float32BufferAttribute;
  private _colAttr: THREE.Float32BufferAttribute;
  /** Tracks whether we already emitted an overflow warning this frame. */
  private _warnedOverflow = false;

  constructor(private scene: THREE.Scene) {
    this.lineGeometry = new THREE.BufferGeometry();
    this.lineMaterial = new THREE.LineBasicMaterial({ vertexColors: true });

    // Pre-allocate persistent buffer attributes at max capacity
    this._posAttr = new THREE.Float32BufferAttribute(new Float32Array(MAX_LINE_POINTS * 3), 3);
    this._posAttr.setUsage(THREE.DynamicDrawUsage);
    this._colAttr = new THREE.Float32BufferAttribute(new Float32Array(MAX_LINE_POINTS * 3), 3);
    this._colAttr.setUsage(THREE.DynamicDrawUsage);
    this.lineGeometry.setAttribute('position', this._posAttr);
    this.lineGeometry.setAttribute('color', this._colAttr);

    this.lineMesh = new THREE.LineSegments(this.lineGeometry, this.lineMaterial);
    this.lineMesh.layers.set(DEBUG_LAYER);
    this.lineMesh.frustumCulled = false;
    this.lineMesh.renderOrder = 9000;
    this.scene.add(this.lineMesh);
  }

  line(from: THREE.Vector3, to: THREE.Vector3, color = 0x00ff00): void {
    if (this.linePositions.length / 3 + 2 > MAX_LINE_POINTS) {
      // Warn once per frame so multi-segment primitives (sphere/box) which
      // emit many `line()` calls don't generate a flood of identical
      // warnings — but the user still gets a clear signal that geometry
      // was silently dropped. Flag is reset in clear() each frame.
      if (!this._warnedOverflow) {
        this._warnedOverflow = true;
        // WHY Logger: engine convention (CLAUDE.md) — console.* bypasses the
        // level filter and the engine log sinks.
        Logger.warn('DebugDraw', `Line buffer full (${MAX_LINE_POINTS} pts); subsequent debug primitives this frame are silently dropped. Increase MAX_LINE_POINTS.`);
      }
      return;
    }

    const r = ((color >> 16) & 0xff) / 255;
    const g = ((color >> 8) & 0xff) / 255;
    const b = (color & 0xff) / 255;

    this.linePositions.push(from.x, from.y, from.z, to.x, to.y, to.z);
    this.lineColors.push(r, g, b, r, g, b);
  }

  sphere(center: THREE.Vector3, radius: number, color = 0x00ff00, segments = 16): void {
    for (let i = 0; i < segments; i++) {
      const a1 = (i / segments) * Math.PI * 2;
      const a2 = ((i + 1) / segments) * Math.PI * 2;

      // XZ ring
      const from = _v.set(
        center.x + Math.cos(a1) * radius,
        center.y,
        center.z + Math.sin(a1) * radius,
      );
      const to = _v2.set(
        center.x + Math.cos(a2) * radius,
        center.y,
        center.z + Math.sin(a2) * radius,
      );
      this.line(from, to, color);

      // XY ring
      from.set(center.x + Math.cos(a1) * radius, center.y + Math.sin(a1) * radius, center.z);
      to.set(center.x + Math.cos(a2) * radius, center.y + Math.sin(a2) * radius, center.z);
      this.line(from, to, color);

      // YZ ring — third equator. Without this, bounding-sphere/trigger-radius
      // overlays show only two flat circles that share no axis with the
      // third dimension, badly misleading for objects moving primarily in
      // YZ (rolling along Z, jumping vertically, etc.).
      from.set(center.x, center.y + Math.sin(a1) * radius, center.z + Math.cos(a1) * radius);
      to.set(center.x, center.y + Math.sin(a2) * radius, center.z + Math.cos(a2) * radius);
      this.line(from, to, color);
    }
  }

  box(center: THREE.Vector3, halfExtents: THREE.Vector3, color = 0x00ff00): void {
    const { x: hx, y: hy, z: hz } = halfExtents;
    const { x: cx, y: cy, z: cz } = center;

    // Bottom face
    this.line(_v.set(cx - hx, cy - hy, cz - hz), _v2.set(cx + hx, cy - hy, cz - hz), color);
    this.line(_v.set(cx + hx, cy - hy, cz - hz), _v2.set(cx + hx, cy - hy, cz + hz), color);
    this.line(_v.set(cx + hx, cy - hy, cz + hz), _v2.set(cx - hx, cy - hy, cz + hz), color);
    this.line(_v.set(cx - hx, cy - hy, cz + hz), _v2.set(cx - hx, cy - hy, cz - hz), color);

    // Top face
    this.line(_v.set(cx - hx, cy + hy, cz - hz), _v2.set(cx + hx, cy + hy, cz - hz), color);
    this.line(_v.set(cx + hx, cy + hy, cz - hz), _v2.set(cx + hx, cy + hy, cz + hz), color);
    this.line(_v.set(cx + hx, cy + hy, cz + hz), _v2.set(cx - hx, cy + hy, cz + hz), color);
    this.line(_v.set(cx - hx, cy + hy, cz + hz), _v2.set(cx - hx, cy + hy, cz - hz), color);

    // Verticals
    this.line(_v.set(cx - hx, cy - hy, cz - hz), _v2.set(cx - hx, cy + hy, cz - hz), color);
    this.line(_v.set(cx + hx, cy - hy, cz - hz), _v2.set(cx + hx, cy + hy, cz - hz), color);
    this.line(_v.set(cx + hx, cy - hy, cz + hz), _v2.set(cx + hx, cy + hy, cz + hz), color);
    this.line(_v.set(cx - hx, cy - hy, cz + hz), _v2.set(cx - hx, cy + hy, cz + hz), color);
  }

  /**
   * Flush debug geometry to GPU. Called once per frame before render.
   */
  flush(): void {
    if (this.linePositions.length === 0) {
      this.lineMesh!.visible = false;
      this.lineGeometry.setDrawRange(0, 0);
      return;
    }

    this.lineMesh!.visible = true;

    const count = this.linePositions.length / 3;
    const posArr = this._posAttr.array as Float32Array;
    const colArr = this._colAttr.array as Float32Array;
    for (let i = 0, len = this.linePositions.length; i < len; i++) {
      posArr[i] = this.linePositions[i];
      colArr[i] = this.lineColors[i];
    }
    this._posAttr.needsUpdate = true;
    this._colAttr.needsUpdate = true;
    this.lineGeometry.setDrawRange(0, count);
    // No computeBoundingSphere() — frustumCulled is false so it's never used
  }

  /**
   * Clear all debug geometry. Called after render.
   */
  clear(): void {
    this.linePositions.length = 0;
    this.lineColors.length = 0;
    this._warnedOverflow = false;
  }

  dispose(): void {
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
    if (this.lineMesh) {
      this.scene.remove(this.lineMesh);
    }
  }
}

// Scratch vectors for debug draw (private to this module)
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
